import * as React from 'react'

import { useDividendEvent } from './hooks'
import { setDividendEventEdit, submitEditDividendEvent } from './model'
import { formatAmount, formatDate, formatPercent, statusColor } from '../utils'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  TimePicker,
  Typography,
} from 'antd'
import { useDividendAccrualList } from './dividend-accruals/hooks'
import { UserAccrualList } from './dividend-accruals/userAccrualListPage'
import { WalletAccrualList } from './dividend-accruals/walletAccrualListPage'
import dayjs from 'dayjs'
import { changedDateFormat } from '../../../../libs/utils'
import {
  CalendarOutlined,
  DollarOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  ProfileOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import { path } from '../../../../libs'

const { Text, Title } = Typography

const panelStyle: React.CSSProperties = {
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
}

const metricCardStyle: React.CSSProperties = {
  ...panelStyle,
  height: '100%',
}

const loadingContainerStyle: React.CSSProperties = {
  minHeight: 360,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const DividendEditDetail = () => {
  const { dividendEvent, dividendEventEdit, loading, updating } =
    useDividendEvent()
  const { dividendAccruals } = useDividendAccrualList()
  const appreciationDividendInfo = dividendEvent.appreciationDividendInfo
  const isLoading = loading || !dividendEvent.dividendEventID

  const isCompleted = dividendEvent.status === 'completed'
  const isPropertyAppreciation = dividendEvent.type === 'propertyAppreciation'
  const currentStatus = dividendEvent.status || 'unknown'
  const currentType = dividendEvent.type || 'type N/A'
  const amountGross = Number(dividendEvent.amount)
  const feeTotal = Number(dividendEvent.feeTotal)
  const amountNet =
    dividendEvent.amount !== '' &&
    dividendEvent.feeTotal !== '' &&
    Number.isFinite(amountGross) &&
    Number.isFinite(feeTotal)
      ? amountGross * (1 - feeTotal / 100)
      : undefined
  const paymentDate = dividendEventEdit.paymentDate
    ? dayjs(dividendEventEdit.paymentDate)
    : null
  const handleChange = (event: {
    currentTarget: { name: any; value: any }
  }) => {
    const { name, value } = event.currentTarget
    setDividendEventEdit({ ...dividendEventEdit, [name]: value })
  }

  const handleFieldChange = (name: string, value: any) => {
    setDividendEventEdit({ ...dividendEventEdit, [name]: value })
  }

  const feeRows = dividendEvent.fees.map((fee, index) => ({
    key: `${fee.Name}-${index}`,
    name: fee.Name,
    percent: fee.Value,
    amount: (+dividendEvent.amount * (+fee.Value / 100)).toFixed(2),
  }))

  if (isLoading) {
    return (
      <div style={loadingContainerStyle}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={{ paddingBottom: 24 }}>
      <div
        style={{
          background: 'var(--crm-bg)',
          borderBottom: '1px solid var(--crm-border)',
          padding: '16px 20px',
          margin: '0 -12px 16px',
        }}
      >
        <Flex align="flex-start" justify="space-between" gap={16} wrap="wrap">
          <Space align="start" size={14}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 8,
                background: '#e6f4ff',
                color: '#1677ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
              }}
            >
              <ProfileOutlined />
            </div>
            <div>
              <Flex align="center" gap={8} wrap="wrap">
                <Title level={5} style={{ margin: 0, fontSize: 18 }}>
                  Edit dividend event {dividendEvent.dividendEventID || '-'}
                </Title>
                {isCompleted && <Tag color="red">Locked</Tag>}
                {isPropertyAppreciation && (
                  <Tag color="orange">Read-only type</Tag>
                )}
              </Flex>
              <Space size={[10, 4]} wrap style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {dividendEvent.name || '-'} / Property ID:{' '}
                  {dividendEvent.propertyId || '-'}
                </Text>
                <Space size={4}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Status:
                  </Text>
                  <Tag
                    color={statusColor(dividendEvent.status)}
                    style={{ margin: 0 }}
                  >
                    {currentStatus}
                  </Tag>
                </Space>
                <Space size={4}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Type:
                  </Text>
                  <Tag color="blue" style={{ margin: 0 }}>
                    {currentType}
                  </Tag>
                </Space>
              </Space>
            </div>
          </Space>

          <Space size={8} wrap>
            <Button
              icon={<EyeOutlined />}
              disabled={!dividendEvent.dividendEventID}
              href={path.nova.dividendEvents.details(
                dividendEvent.dividendEventID,
              )}
            >
              Details
            </Button>
          </Space>
        </Flex>
      </div>

      {isCompleted && (
        <Alert
          showIcon
          type="info"
          style={{ marginBottom: 16 }}
          message="Completed dividend events are read-only."
        />
      )}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Amount Gross"
              value={
                isPropertyAppreciation
                  ? appreciationDividendInfo?.finalPropertyPrice
                  : formatAmount(dividendEvent.amount)
              }
              prefix={<DollarOutlined />}
              suffix="USD"
            />
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Amount Net"
              value={
                isPropertyAppreciation
                  ? appreciationDividendInfo?.totalNetValue
                  : formatAmount(amountNet)
              }
              prefix={<DollarOutlined />}
              suffix="USD"
            />
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Fee Total"
              value={
                isPropertyAppreciation
                  ? appreciationDividendInfo?.appreciationFeePercent
                  : formatAmount(dividendEvent.feeTotal)
              }
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Statistic
              title="Payment Date"
              value={formatDate(dividendEvent.paymentDate)}
              prefix={<CalendarOutlined />}
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} xl={15}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card
              title="Event overview"
              style={panelStyle}
              extra={
                <Tag color="geekblue">
                  Stage {dividendEvent.stage} / {dividendEvent.stages}
                </Tag>
              }
            >
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Investment strategy">
                  {dividendEvent.investmentStrategy.name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Annual dividends">
                  {formatPercent(dividendEvent.annualDividends)}
                </Descriptions.Item>
                <Descriptions.Item label="Token amount">
                  {dividendEvent.tokenAmount || '-'}
                </Descriptions.Item>
                {isPropertyAppreciation && (
                  <>
                    <Descriptions.Item label="Initial property price">
                      {appreciationDividendInfo?.initialPropertyPrice ?? '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Final property price">
                      {appreciationDividendInfo?.finalPropertyPrice ?? '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Final property appreciation">
                      {appreciationDividendInfo?.finalPropertyAppreciation ??
                        '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Appreciation fee">
                      {appreciationDividendInfo?.appreciationFeeValue
                        ? `${formatPercent(
                            appreciationDividendInfo.appreciationFeePercent,
                          )} of ${
                            appreciationDividendInfo.appreciationFeeValue
                          } USD`
                        : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total NET value">
                      {appreciationDividendInfo?.totalNetValue ?? '-'}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

            {!isPropertyAppreciation && (
              <Card title="Fees breakdown" style={panelStyle}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  dataSource={feeRows}
                  columns={[
                    {
                      title: 'Fee',
                      dataIndex: 'name',
                    },
                    {
                      title: 'Percent',
                      dataIndex: 'percent',
                      render: (value) => `${value}%`,
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      render: (value) => `${value} USD`,
                    },
                  ]}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <Text strong>Total fee</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <Text strong>
                          {formatPercent(dividendEvent.feeTotal)}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        <Text strong>
                          {formatAmount(amountGross * (feeTotal / 100))} USD
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </Card>
            )}
          </Space>
        </Col>

        <Col xs={24} xl={9}>
          <Card
            title="Editable values"
            style={{ ...panelStyle, height: '100%' }}
          >
            {isPropertyAppreciation && (
              <Alert
                showIcon
                type="info"
                style={{ marginBottom: 16 }}
                message="Updates are disabled for property appreciation events."
              />
            )}

            <Form layout="vertical">
              <Form.Item label="New amount">
                <Input
                  name="amount"
                  value={dividendEventEdit.amount}
                  onChange={handleChange}
                  disabled={isCompleted || isPropertyAppreciation}
                  prefix={<DollarOutlined />}
                />
              </Form.Item>

              <Form.Item label="New status">
                <Select
                  value={dividendEventEdit.status}
                  onChange={(value) => handleFieldChange('status', value)}
                  disabled={isCompleted || isPropertyAppreciation}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="created">created</Select.Option>
                  <Select.Option value="executable">executable</Select.Option>
                  {isCompleted && (
                    <Select.Option value="completed">completed</Select.Option>
                  )}
                </Select>
              </Form.Item>

              <Row gutter={12}>
                <Col xs={24} md={12} xl={24} xxl={12}>
                  <Form.Item label="New payment date">
                    {isCompleted ? (
                      <Text>
                        {changedDateFormat(
                          new Date(dividendEventEdit.paymentDate),
                        )}
                      </Text>
                    ) : (
                      <DatePicker
                        value={paymentDate}
                        disabled={isPropertyAppreciation}
                        style={{ width: '100%' }}
                        suffixIcon={<CalendarOutlined />}
                        onChange={(date) => {
                          if (date) {
                            const existing = new Date(
                              dividendEventEdit.paymentDate,
                            )
                            const newDate = date.toDate()
                            newDate.setHours(
                              existing.getHours(),
                              existing.getMinutes(),
                              existing.getSeconds(),
                              existing.getMilliseconds(),
                            )
                            handleFieldChange('paymentDate', newDate.getTime())
                          }
                        }}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} xl={24} xxl={12}>
                  <Form.Item label="New payment time">
                    {isCompleted ? (
                      <Text>
                        {new Date(dividendEventEdit.paymentDate).toTimeString()}
                      </Text>
                    ) : (
                      <TimePicker
                        value={paymentDate}
                        disabled={isPropertyAppreciation}
                        style={{ width: '100%' }}
                        suffixIcon={<FieldTimeOutlined />}
                        onChange={(time) => {
                          if (time) {
                            const existing = new Date(
                              dividendEventEdit.paymentDate,
                            )
                            existing.setHours(
                              time.hour(),
                              time.minute(),
                              time.second(),
                              0,
                            )
                            handleFieldChange('paymentDate', existing.getTime())
                          }
                        }}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              <Button
                type="primary"
                block
                size="large"
                icon={<SaveOutlined />}
                loading={updating}
                disabled={updating || isCompleted || isPropertyAppreciation}
                onClick={() => submitEditDividendEvent(dividendEventEdit)}
              >
                Save changes
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {dividendAccruals.userDividendAccruals.length > 0 && (
          <Card style={panelStyle} bodyStyle={{ padding: 0 }}>
            <UserAccrualList />
          </Card>
        )}
        {dividendAccruals.walletDividendAccruals.length > 0 && (
          <Card style={panelStyle} bodyStyle={{ padding: 0 }}>
            <WalletAccrualList />
          </Card>
        )}
      </Space>
    </div>
  )
}
